// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { objDefine, strSubstr } from "@nevware21/ts-utils";
import { _DYN_ATTRIBUTES, _DYN_GET_TRACE_CTX, _DYN_SPAN_ID, _DYN_START_TIME } from "../../../__DynamicConstants";
import { createDistributedTraceContext } from "../../../core/TelemetryHelpers";
import { generateW3CId } from "../../../utils/CoreUtils";
import { createSpan } from "./span";
/**
 * @internal
 * Creates a new trace provider adapter
 * @param host - The trace host instance (typically IAppInsightsCore).
 * @param traceName - The name of the trace provider.
 * @param api - The OpenTelemetry API instance (as a lazy value).
 * @param onEnd - Optional callback to be invoked when a span ends.
 * @param onException - Optional callback to be invoked when an exception is recorded on a span.
 * @returns The created trace provider.
 */
/*#__NO_SIDE_EFFECTS__*/
export function createTraceProvider(host, traceName, api, onEnd, onException) {
    var provider = {
        api: null,
        createSpan: function (name, options, parent) {
            var newCtx;
            var parentCtx;
            if (options && options.root) {
                newCtx = createDistributedTraceContext();
            }
            else {
                newCtx = createDistributedTraceContext(parent || host[_DYN_GET_TRACE_CTX /* @min:%2egetTraceCtx */]());
                if (newCtx.parentCtx) {
                    parentCtx = newCtx.parentCtx;
                }
            }
            // Always generate a new spanId
            newCtx[_DYN_SPAN_ID /* @min:%2espanId */] = strSubstr(generateW3CId(), 0, 16);
            var spanCtx = {
                api: api,
                spanContext: newCtx,
                attributes: options ? options[_DYN_ATTRIBUTES /* @min:%2eattributes */] : undefined,
                startTime: options ? options[_DYN_START_TIME /* @min:%2estartTime */] : undefined,
                isRecording: options ? options.recording !== false : true,
                onEnd: onEnd,
                onException: onException
            };
            if (parentCtx) {
                objDefine(spanCtx, "parentSpanContext", {
                    v: parentCtx,
                    w: false
                });
            }
            return createSpan(spanCtx, name, (options === null || options === void 0 ? void 0 : options.kind) || 0 /* eOTelSpanKind.INTERNAL */);
        },
        getProviderId: function () { return traceName; },
        isAvailable: function () { return !!onEnd; }
    };
    objDefine(provider, "api", {
        v: api,
        w: false
    });
    return provider;
}
//# sourceMappingURL=traceProvider.js.map